import {
  ContextMenuItem,
  ContextMenuSub,
  ContextMenuSubContent,
  ContextMenuSubTrigger,
} from "@/components/ui/shadcn/ContextMenu/ContextMenuPrimitives";
import { Checkbox, Input, LoadingSpinner } from "@/components/ui";
import { Fragment, useMemo, useRef, useState, type ReactNode } from "react";

interface SearchableContextMenuSubmenuProps<T> {
  closeOnSelect?: boolean;
  disabledItemKeys?: string[];
  emptyMessage?: string;
  footer?: ReactNode;
  getItemGroup?: (item: T) => string;
  getItemKey: (item: T) => string;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  isLoading?: boolean;
  items: T[];
  onLoadMore?: () => void;
  onOpenChange?: (open: boolean) => void;
  onSearchChange: (search: string) => void;
  onSelectItem: (item: T) => void;
  placeholder?: string;
  renderItem: (item: T) => ReactNode;
  search: string;
  selectedItemKeys?: string[];
  selectionMode?: "multiple" | "single";
  title: ReactNode;
}

/** A reusable context-menu submenu for server-backed, paginated selections. */
export const SearchableContextMenuSubmenu = <T,>({
  closeOnSelect,
  disabledItemKeys = [],
  emptyMessage = "No results found.",
  footer,
  getItemGroup,
  getItemKey,
  hasNextPage = false,
  isFetchingNextPage = false,
  isLoading = false,
  items,
  onLoadMore,
  onOpenChange,
  onSearchChange,
  onSelectItem,
  placeholder = "Search...",
  renderItem,
  search,
  selectedItemKeys = [],
  selectionMode = "single",
  title,
}: SearchableContextMenuSubmenuProps<T>) => {
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [prioritizedItemKeys, setPrioritizedItemKeys] = useState<string[]>([]);
  const shouldCloseOnSelect = closeOnSelect ?? selectionMode !== "multiple";
  const prioritizedItemKeySet = useMemo(
    () => new Set(prioritizedItemKeys),
    [prioritizedItemKeys],
  );
  const orderedItems = useMemo(() => {
    if (prioritizedItemKeys.length === 0 || getItemGroup) {
      return items;
    }

    return [...items].sort(
      (first, second) =>
        Number(prioritizedItemKeySet.has(getItemKey(second))) -
        Number(prioritizedItemKeySet.has(getItemKey(first))),
    );
  }, [
    getItemGroup,
    getItemKey,
    items,
    prioritizedItemKeys,
    prioritizedItemKeySet,
  ]);
  const firstUnprioritizedItemIndex = orderedItems.findIndex(
    (item) => !prioritizedItemKeySet.has(getItemKey(item)),
  );
  const focusSearchInput = () => {
    const input = searchInputRef.current;
    if (input && document.activeElement !== input) {
      input.focus();
    }
  };

  return (
    <ContextMenuSub
      onOpenChange={(open) => {
        if (open) {
          setPrioritizedItemKeys(selectedItemKeys);
        }
        onOpenChange?.(open);
      }}
    >
      <ContextMenuSubTrigger>{title}</ContextMenuSubTrigger>
      <ContextMenuSubContent
        className="w-72 p-0"
        onMouseMove={focusSearchInput}
      >
        <div className="border-border border-b px-3 py-0.5">
          <Input
            ref={searchInputRef}
            value={search}
            onChange={(event) => {
              onSearchChange(event.target.value);
            }}
            onKeyDown={(event) => {
              event.stopPropagation();
            }}
            placeholder={placeholder}
            className="placeholder:text-muted-foreground/80 focus:placeholder:text-foreground/70 h-7 rounded-none border-0 bg-transparent px-0 py-0 leading-7 shadow-none transition-colors focus-visible:ring-0 focus-visible:ring-offset-0"
          />
        </div>
        <div
          className="max-h-64 overflow-y-auto p-1"
          onScroll={(event) => {
            const element = event.currentTarget;
            const isNearBottom =
              element.scrollTop + element.clientHeight >=
              element.scrollHeight - 16;
            if (isNearBottom && hasNextPage && !isFetchingNextPage) {
              onLoadMore?.();
            }
          }}
        >
          {orderedItems.map((item, index) => (
            <Fragment key={getItemKey(item)}>
              {getItemGroup &&
              (index === 0 ||
                getItemGroup(orderedItems[index - 1]) !==
                  getItemGroup(item)) ? (
                <div className="text-primary px-2 py-1.5 text-xs font-medium">
                  {getItemGroup(item)}
                </div>
              ) : null}
              {selectionMode === "multiple" &&
              prioritizedItemKeys.length > 0 &&
              index === firstUnprioritizedItemIndex ? (
                <div className="border-border -mx-1 my-1 border-t" />
              ) : null}
              <ContextMenuItem
                className="group hover:bg-neutral-200"
                selected={
                  selectionMode === "single" &&
                  selectedItemKeys.includes(getItemKey(item))
                }
                disabled={disabledItemKeys.includes(getItemKey(item))}
                onSelect={(event) => {
                  if (!shouldCloseOnSelect) {
                    event.preventDefault();
                  }
                  onSelectItem(item);
                }}
              >
                {selectionMode === "multiple" ? (
                  <Checkbox
                    aria-hidden
                    checked={selectedItemKeys.includes(getItemKey(item))}
                    className={`pointer-events-none h-3.5 w-3.5 transition-opacity [&_svg]:h-3 [&_svg]:w-3 ${
                      selectedItemKeys.includes(getItemKey(item))
                        ? "opacity-100"
                        : "opacity-0 group-hover:opacity-100 group-focus:opacity-100"
                    }`}
                    tabIndex={-1}
                  />
                ) : null}
                {renderItem(item)}
              </ContextMenuItem>
            </Fragment>
          ))}
          {isLoading || isFetchingNextPage ? (
            <div className="flex justify-center p-2">
              <LoadingSpinner className="h-4 w-4" />
            </div>
          ) : null}
          {!isLoading && items.length === 0 ? (
            <p className="text-muted-foreground px-2 py-3 text-sm">
              {emptyMessage}
            </p>
          ) : null}
          {footer ? (
            <div className="border-border mt-1 border-t p-1">{footer}</div>
          ) : null}
        </div>
      </ContextMenuSubContent>
    </ContextMenuSub>
  );
};
